import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Image as ImageIcon, Music, Send, TrendingUp, Crown, Play, Pause } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import PostCard, { Avatar } from "@/components/PostCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import TopTracksChart from "@/components/TopTracksChart";
import LivePartiesCard from "@/components/LivePartiesCard";
import UpcomingPartiesCard from "@/components/UpcomingPartiesCard";
import { toast } from "sonner";
import { Link as RLink } from "react-router-dom";

function Composer({ onPosted }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [myTracks, setMyTracks] = useState([]);
  const [attached, setAttached] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/tracks").then(({ data }) => setMyTracks(data.filter((t) => t.user_id === user.id))).catch(() => {});
  }, [user.id]);

  const post = async () => {
    if (!text.trim() && !attached) return;
    setBusy(true);
    try {
      const { data } = await api.post("/posts", { text, track_id: attached });
      onPosted(data); setText(""); setAttached(null);
    } catch { toast.error("Could not post"); }
    finally { setBusy(false); }
  };

  return (
    <div className="nu-card p-5" data-testid="post-composer">
      <div className="flex gap-3">
        <Avatar artist={user} />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
          placeholder="Share an update, a drop, a thought…" data-testid="composer-text"
          className="flex-1 bg-transparent resize-none outline-none text-zinc-100 placeholder:text-zinc-500 pt-2" />
      </div>
      {attached && (
        <div className="ml-14 mb-2 text-xs text-emerald-400 font-mono-nu">
          ♫ {myTracks.find((t) => t.id === attached)?.title} <button onClick={() => setAttached(null)} className="text-zinc-500 ml-2">remove</button>
        </div>
      )}
      <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3">
        <div className="flex items-center gap-2">
          {myTracks.length > 0 && (
            <select data-testid="composer-attach-track" value={attached || ""} onChange={(e) => setAttached(e.target.value || null)}
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-zinc-300 outline-none">
              <option value="">Attach a track…</option>
              {myTracks.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          )}
        </div>
        <button onClick={post} disabled={busy} data-testid="composer-submit"
          className="flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm transition-colors disabled:opacity-50">
          <Send className="w-4 h-4" /> Post
        </button>
      </div>
    </div>
  );
}

function TrendingTrack({ track }) {
  const { playTrack, current, isPlaying, togglePlay } = usePlayer();
  const active = current?.id === track.id;
  return (
    <div className="flex items-center gap-3 group">
      {track.cover_url ? <img src={mediaUrl(track.cover_url)} className="w-11 h-11 rounded-lg object-cover" alt="" />
        : <span className="w-11 h-11 rounded-lg bg-emerald-500/15 flex items-center justify-center"><Music className="w-5 h-5 text-emerald-400" /></span>}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{track.title}</p>
        <p className="text-xs text-zinc-500 truncate flex items-center gap-1"><Play className="w-3 h-3" /> {track.plays || 0} · {track.artist?.artist_name}</p>
      </div>
      <button onClick={() => (active ? togglePlay() : playTrack(track, []))} data-testid="trending-track-play"
        className="w-8 h-8 rounded-full bg-white/5 group-hover:bg-emerald-500 group-hover:text-black text-emerald-400 flex items-center justify-center transition-colors">
        {active && isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
    </div>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [f, t, a] = await Promise.all([api.get("/feed"), api.get("/tracks"), api.get("/artists")]);
      setPosts(f.data); setTracks(t.data.slice(0, 5)); setArtists(a.data.filter((x) => x.id !== user.id).slice(0, 4));
    } catch {} finally { setLoading(false); }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("nu:refresh", h);
    return () => window.removeEventListener("nu:refresh", h);
  }, [load]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {!user.is_subscribed && (
        <Link to="/subscribe" data-testid="feed-subscribe-banner"
          className="flex items-center justify-between gap-4 beam-border rounded-2xl px-5 py-4 mb-6">
          <div className="flex items-center gap-3">
            <Crown className="w-6 h-6 text-amber-400 shrink-0" />
            <div><p className="font-bold">Unlock the Artist Pass</p><p className="text-sm text-zinc-400">Upload music & videos, send DMs, and go pro.</p></div>
          </div>
          <span className="px-4 py-2 rounded-full bg-emerald-500 text-black font-bold text-sm shrink-0">$25/mo</span>
        </Link>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <LivePartiesCard />
          <Composer onPosted={(p) => setPosts([p, ...posts])} />
          {loading ? <p className="text-zinc-500 text-center py-12 font-mono-nu">loading feed…</p>
            : posts.length === 0 ? <p className="text-zinc-500 text-center py-12">No posts yet. Be the first to share!</p>
            : posts.map((p) => <PostCard key={p.id} post={p} />)}
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <TopTracksChart />
          <UpcomingPartiesCard />
          <div className="nu-card p-5">
            <h3 className="flex items-center gap-2 font-bold mb-4"><TrendingUp className="w-4 h-4 text-emerald-400" /> Fresh Drops</h3>
            <div className="space-y-4">{tracks.map((t) => <TrendingTrack key={t.id} track={t} />)}</div>
          </div>
          <div className="nu-card p-5">
            <h3 className="font-bold mb-4">Artists to follow</h3>
            <div className="space-y-3">
              {artists.map((a) => (
                <Link key={a.id} to={`/artist/${a.id}`} className="flex items-center gap-3 group">
                  <Avatar artist={a} size={38} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate group-hover:text-emerald-400 transition-colors flex items-center gap-1">{a.artist_name}<VerifiedBadge show={a.is_subscribed} size={13} /></p>
                    <p className="text-xs text-zinc-500 truncate">{a.genre || `@${a.handle}`}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
